import React from 'react';
import { valuePillars } from '../../data/valuePillars';
import { ShieldCheck, Target, Sparkles, CheckCircle2 } from 'lucide-react';
import { Badge, Card } from '../ui';

export const ValuePillarsSection: React.FC = () => {
  return (
    <section id="why-us" className="py-12 sm:py-14 lg:py-16 relative bg-white dark:bg-[#080e1a] transition-colors">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
        
        {/* Technook Original Header Layout */}
        <div className="max-w-3xl mb-8 sm:mb-10 text-left space-y-3">
          <Badge variant="brand" className="px-3.5 py-1 text-[11px] sm:text-xs uppercase tracking-wider">
            WHY TECHNOOK GLOBAL
          </Badge>
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-extrabold text-[#184166] dark:text-white tracking-tight">
            Engineering with clear business purpose.
          </h2>
          <p className="text-slate-600 dark:text-slate-300 text-sm sm:text-base leading-relaxed">
            We focus on long-term client value, smart costing, and rapid delivery — ensuring every line of code directly drives measurable business growth.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
          
          {/* Left Column: Technook Original Progress Bars */}
          <div className="lg:col-span-7 space-y-4 sm:space-y-6 flex flex-col justify-between">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {valuePillars.map((pillar) => (
                <Card
                  key={pillar.id}
                  variant="glass"
                  className="p-5 sm:p-6 bg-[#f5f9fc] dark:bg-[#0f1b2d] border border-slate-200 dark:border-slate-800 space-y-3 shadow-md"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-[#184166] dark:text-white uppercase tracking-wider">
                      {pillar.label}
                    </span>
                    <span className="text-sm font-black text-[#08adae] font-mono">
                      {pillar.emphasis}%
                    </span>
                  </div>

                  <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                    {pillar.copy}
                  </p>

                  {/* Technook Horizontal Progress Bar */}
                  <div className="w-full bg-slate-200 dark:bg-slate-800 rounded-full h-2 overflow-hidden">
                    <div
                      className="bg-gradient-to-r from-[#4a82c2] to-[#08adae] h-2 rounded-full transition-all duration-1000"
                      style={{ width: `${pillar.emphasis}%` }}
                    />
                  </div>
                </Card>
              ))}
            </div>

            {/* Micro Guarantee Card */}
            <Card variant="glass" className="p-4 sm:p-5 bg-sky-50/70 dark:bg-slate-900/80 border border-[#4a82c2]/30 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-[#4a82c2]/10 text-[#4a82c2]">
                  <ShieldCheck size={20} />
                </div>
                <div>
                  <h4 className="text-xs font-bold text-[#184166] dark:text-white">Parañaque Engineering Leadership</h4>
                  <p className="text-[11px] text-slate-600 dark:text-slate-400">Onsite technical strategy & agile delivery guarantees</p>
                </div>
              </div>
              <Badge variant="accent" className="text-[10px] shrink-0">
                100% Quality Sign-Off
              </Badge>
            </Card>
          </div>

          {/* Right Column: Technook Tall Purpose Card */}
          <div className="lg:col-span-5">
            <Card
              variant="glass"
              className="h-full p-6 sm:p-8 bg-[#f5f9fc] dark:bg-[#0f1b2d] border border-slate-200 dark:border-slate-800 flex flex-col justify-between relative overflow-hidden shadow-xl"
            >
              {/* Teal Vertical Accent Bar */}
              <div className="absolute top-0 left-0 w-2 h-full bg-[#08adae]" />

              <div className="space-y-4 relative z-10 pl-2">
                <div className="p-3 rounded-2xl bg-[#08adae]/10 text-[#08adae] w-fit">
                  <Target size={26} />
                </div>

                <h3 className="text-xl sm:text-2xl font-extrabold text-[#184166] dark:text-white tracking-tight">
                  Technology with a clear purpose.
                </h3>

                <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                  Technook doesn&apos;t just write code — we build sustainable business systems that improve team productivity, reduce operational friction, and generate predictable ROI for your organization.
                </p>

                <div className="space-y-2 pt-3 border-t border-slate-200 dark:border-slate-800 text-xs">
                  <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300 font-semibold">
                    <CheckCircle2 size={15} className="text-[#08adae] shrink-0" />
                    <span>Direct Access to Senior Lead Architects</span>
                  </div>
                  <div className="flex items-center gap-2 text-[#08adae] dark:text-slate-300 font-semibold">
                    <CheckCircle2 size={15} className="text-[#08adae] shrink-0" />
                    <span>Clean Modular Codebases (Zero Tech Debt)</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300 font-semibold">
                    <CheckCircle2 size={15} className="text-[#08adae] shrink-0" />
                    <span>Transparent Milestone & Budget Tracking</span>
                  </div>
                </div>
              </div>

              <div className="pt-6 mt-6 border-t border-slate-200 dark:border-slate-800/80 flex items-center justify-between relative z-10 pl-2">
                <span className="text-[11px] font-mono text-[#4a82c2] font-semibold">Imperial Plaza HQ</span>
                <span className="text-xs font-bold text-[#08adae] flex items-center gap-1">
                  <Sparkles size={14} /> Agile Delivery
                </span>
              </div>
            </Card>
          </div>

        </div>
      </div>
    </section>
  );
};
